import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';
import countries from 'world-countries';

export const normalizeDialCode = (dialCode?: string | null): string => {
  if (!dialCode) return '';
  const trimmed = dialCode.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
};

export const getDialCodeFromCountryShortCode = (
  countryShortCode?: string
): string => {
  if (!countryShortCode) return '';

  const countryMeta = countries.find((c) => c.cca2 === countryShortCode);
  return normalizeDialCode(
    `${countryMeta?.idd?.root || ''}${countryMeta?.idd?.suffixes?.[0] || ''}`
  );
};

export const getCountryShortCodeFromDialCode = (
  dialCode?: string | null
): string => {
  const normalizedDialCode = normalizeDialCode(dialCode);
  if (!normalizedDialCode) return '';

  const countryMeta = countries.find(
    (c) =>
      normalizeDialCode(`${c.idd?.root || ''}${c.idd?.suffixes?.[0] || ''}`) ===
      normalizedDialCode
  );

  return countryMeta?.cca2 || '';
};

export const resolvePhoneCountryShortCode = (
  dialCode?: string | null,
  phoneCountryShortCode?: string,
  fallbackCountryShortCode?: string
): string => {
  const normalizedDialCode = normalizeDialCode(dialCode);

  if (normalizedDialCode) {
    const phoneCountryDialCode =
      getDialCodeFromCountryShortCode(phoneCountryShortCode);

    if (phoneCountryDialCode === normalizedDialCode) {
      return phoneCountryShortCode || '';
    }

    return (
      getCountryShortCodeFromDialCode(normalizedDialCode) ||
      phoneCountryShortCode ||
      fallbackCountryShortCode ||
      ''
    );
  }

  return phoneCountryShortCode || fallbackCountryShortCode || '';
};

/**
 * Normalizes a national phone number based on the country short code.
 * Specifically, strips the domestic trunk prefix (e.g. leading '0') if present
 * for countries where it is not part of the international number (like Finland, Germany).
 */
export function normalizeNationalPhoneNumber(
  phone: string,
  countryShortCode?: string
): string {
  if (!phone) return '';

  // Clean all characters except digits (and leading + if present)
  const cleaned = phone.replace(/[^\d+]/g, '');

  if (countryShortCode) {
    try {
      const phoneNumber = parsePhoneNumberFromString(
        cleaned,
        countryShortCode as CountryCode
      );
      if (phoneNumber) {
        return phoneNumber.nationalNumber;
      }
    } catch (e) {
      console.error('Error parsing phone number with libphonenumber-js:', e);
    }
  }

  // Generic fallback: strip leading zero if it starts with '0'
  if (cleaned.startsWith('0')) {
    return cleaned.slice(1);
  }
  return cleaned;
}

function formatFinnishSpaces(formatted: string): string {
  const cleaned = formatted.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+358')) {
    let national = cleaned.slice(4);
    if (national.startsWith('0')) {
      national = national.slice(1);
    }
    let prefix = '';

    if (national.startsWith('457')) {
      prefix = '457';
    } else if (
      /^[23689]/.test(national) ||
      (national.startsWith('5') && !national.startsWith('50'))
    ) {
      prefix = national.slice(0, 1);
    } else if (national.length >= 2) {
      prefix = national.slice(0, 2);
    }

    if (prefix) {
      const subscriber = national.slice(prefix.length);
      let formattedSubscriber = subscriber;
      if (subscriber.length === 7) {
        formattedSubscriber = `${subscriber.slice(0, 3)} ${subscriber.slice(3)}`;
      } else if (subscriber.length === 6) {
        formattedSubscriber = `${subscriber.slice(0, 3)} ${subscriber.slice(3)}`;
      } else if (subscriber.length === 8) {
        formattedSubscriber = `${subscriber.slice(0, 3)} ${subscriber.slice(3, 6)} ${subscriber.slice(6)}`;
      } else if (subscriber.length === 9) {
        formattedSubscriber = `${subscriber.slice(0, 3)} ${subscriber.slice(3, 6)} ${subscriber.slice(6)}`;
      } else if (subscriber.length === 5) {
        formattedSubscriber = `${subscriber.slice(0, 2)} ${subscriber.slice(2)}`;
      } else if (subscriber.length === 4) {
        formattedSubscriber = `${subscriber.slice(0, 2)} ${subscriber.slice(2)}`;
      }
      return `+358 ${prefix} ${formattedSubscriber}`;
    }
  }
  return formatted;
}

/**
 * Formats a phone number to standard international format (+[CountryCode] [NationalNumber])
 */
export function formatInternationalPhoneNumber(
  phone?: string,
  countryCode?: string,
  countryShortCode?: string
): string {
  if (!phone) return '';

  // Clean all characters except digits and +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // If the number already contains + indicating it is an international format
  if (cleaned.startsWith('+')) {
    try {
      const parsed = parsePhoneNumberFromString(cleaned);
      if (parsed) return formatFinnishSpaces(parsed.formatInternational());
    } catch {
      console.error('');
    }
  }

  // Phone ISO is the calling-code identity. Business-address dial (country_code) must
  // not override it when Country and telephone calling code legitimately differ.
  if (countryShortCode) {
    try {
      const parsed = parsePhoneNumberFromString(
        cleaned,
        countryShortCode as CountryCode
      );
      if (parsed) return formatFinnishSpaces(parsed.formatInternational());
    } catch {
      console.error('');
    }
  }

  if (countryCode) {
    const cleanCode = countryCode.startsWith('+')
      ? countryCode
      : `+${countryCode}`;
    try {
      const parsed = parsePhoneNumberFromString(`${cleanCode}${cleaned}`);
      if (parsed) return formatFinnishSpaces(parsed.formatInternational());
      // eslint-disable-next-line no-empty
    } catch {}
    return formatFinnishSpaces(`${cleanCode}${cleaned}`);
  }

  return formatFinnishSpaces(phone);
}

export function getDialCodeFromIso(
  iso?: string,
  fallbackMap?: Record<string, { secondary: string }>
): string {
  if (!iso) return '';
  const fromMap = fallbackMap?.[iso]?.secondary;
  if (fromMap && fromMap.startsWith('+')) return fromMap;
  try {
    const parsed = parsePhoneNumberFromString('00', iso as CountryCode);
    if (parsed?.countryCallingCode) return `+${parsed.countryCallingCode}`;
  } catch {
    /* ignore */
  }
  return '';
}
